"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { RollLineRow } from "@/components/character/roll-line-row";
import { useCharacterSheet } from "@/hooks/use-character-sheet";
import type {
  PostRollFeedbackDecision,
  PostRollFeedbackEnvelope,
} from "@/lib/core/schemas/post-roll-feedback.schema";
import type {
  SaveRoll,
  SaveRollResult,
} from "@/lib/core/schemas/save-roll.schema";
import logger from "@/lib/logger";
import { displayThresholdFromResolutionPlan } from "@/lib/systems/save-rolls/flat-threshold-plan";
import { cn } from "@/lib/utils";
import { callMcpTool } from "@/src/api/mcp";
import { PostRollFeedbackOffer } from "./post-roll-feedback-offer";
import { useSaveRollAnimation } from "./save-roll-animation-context";
import "./skill-check-card.css";
import { DEFAULT_MODIFIERS } from "./skill-check-modifiers";

type SaveRollCardProps = {
  saveRoll: SaveRoll;
  campaignId: string;
  onResolved?: () => void;
};

// ── Helpers ──────────────────────────────────────────────────────────

function getOutcomeColor(result: SaveRollResult): string {
  if (result.isCriticalSuccess) {
    return "text-yellow-400";
  }
  if (result.isCriticalFailure) {
    return "text-red-700";
  }
  if (result.thresholdMet) {
    return "text-green-500";
  }
  return "text-red-500";
}

function getOutcomeBorderColor(result: SaveRollResult): string {
  if (result.isCriticalSuccess) {
    return "border-yellow-400";
  }
  if (result.isCriticalFailure) {
    return "border-red-700";
  }
  if (result.thresholdMet) {
    return "border-green-500";
  }
  return "border-red-500";
}

function getOutcomeBadge(result: SaveRollResult): {
  label: string;
  className: string;
} {
  if (result.isCriticalSuccess) {
    return {
      label: "Critical Success",
      className: "bg-yellow-400/20 text-yellow-300",
    };
  }
  if (result.isCriticalFailure) {
    return {
      label: "Critical Failure",
      className: "bg-red-700/20 text-red-400",
    };
  }
  if (result.thresholdMet) {
    return {
      label: "Success",
      className: "bg-green-500/20 text-green-400",
    };
  }
  return {
    label: "Failure",
    className: "bg-red-500/20 text-red-400",
  };
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") {
    return true;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// ── Animation hook ──────────────────────────────────────────────────

type AnimationPhase =
  | "idle"
  | "dim"
  | "spin"
  | "cycling"
  | "landed"
  | "badge"
  | "done";

function useRollAnimation(
  result: SaveRollResult | null,
  shouldAnimate: boolean,
  isDramatic: boolean
): {
  phase: AnimationPhase;
  displayValue: number;
  start: () => void;
} {
  const [phase, setPhase] = useState<AnimationPhase>("idle");
  const [displayValue, setDisplayValue] = useState(1);
  const rafRef = useRef<number>(0);
  const timeoutRefs = useRef<ReturnType<typeof setTimeout>[]>([]);

  const cleanup = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    for (const t of timeoutRefs.current) {
      clearTimeout(t);
    }
    timeoutRefs.current = [];
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(() => {
    if (!result || !shouldAnimate) {
      setPhase("done");
      return;
    }

    if (prefersReducedMotion()) {
      setDisplayValue(result.roll);
      setPhase("done");
      return;
    }

    cleanup();

    const cycleDuration = isDramatic ? 400 : 150;
    const scheduleTimeout = (fn: () => void, ms: number) => {
      const id = setTimeout(fn, ms);
      timeoutRefs.current.push(id);
    };

    const startCycling = (duration: number, onDone: () => void) => {
      const startTime = performance.now();
      const tick = (now: number) => {
        if (now - startTime >= duration) {
          setDisplayValue(result.roll);
          onDone();
          return;
        }
        setDisplayValue(Math.floor(Math.random() * 20) + 1);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    if (isDramatic) {
      setPhase("dim");
      scheduleTimeout(() => {
        setPhase("spin");
        scheduleTimeout(() => {
          setPhase("cycling");
          startCycling(cycleDuration, () => {
            setPhase("landed");
            scheduleTimeout(() => {
              setPhase("badge");
              scheduleTimeout(() => {
                setPhase("done");
              }, 300);
            }, 400);
          });
        }, 400);
      }, 200);
    } else {
      setPhase("cycling");
      startCycling(cycleDuration, () => {
        setPhase("landed");
        scheduleTimeout(() => {
          setPhase("badge");
          scheduleTimeout(() => {
            setPhase("done");
          }, 200);
        }, 250);
      });
    }
  }, [result, shouldAnimate, isDramatic, cleanup]);

  return { phase, displayValue, start };
}

// ── Component ────────────────────────────────────────────────────────

export function SaveRollCard({
  saveRoll,
  campaignId,
  onResolved,
}: SaveRollCardProps) {
  const { animationCount, incrementAnimationCount } = useSaveRollAnimation();

  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [animating, setAnimating] = useState(false);
  const [localResult, setLocalResult] = useState<SaveRollResult | null>(null);
  const [pendingPostRollFeedback, setPendingPostRollFeedback] = useState<
    PostRollFeedbackEnvelope | null | undefined
  >(undefined);
  const animationTriggered = useRef(false);

  // Normalise the saveRoll object for rendering
  const saveRollId = saveRoll.id;
  const savingAbility = saveRoll.savingAbility;
  // The displayed DC is derived from the resolutionPlan's first `gte`
  // threshold (the stored flat `dc` field was retired in Slice 2). A
  // function-inspector-only plan carries no flat threshold → render "?".
  const dc =
    saveRoll.status === "cancelled"
      ? null
      : displayThresholdFromResolutionPlan(saveRoll.resolutionPlan);
  const dcLabel = dc ?? "?";
  const status = saveRoll.status;

  const resolvedResult =
    saveRoll.status === "resolved" ? saveRoll.result : null;
  const cancelledReason =
    saveRoll.status === "cancelled" ? saveRoll.cancelledReason : null;

  const { sheet } = useCharacterSheet(campaignId, saveRoll.actorId);

  const activeResult = localResult ?? resolvedResult;
  const activePostRollFeedback =
    pendingPostRollFeedback !== undefined
      ? pendingPostRollFeedback
      : saveRoll.status === "pending"
        ? (saveRoll.pendingPostRollFeedback ?? null)
        : null;

  const isDramatic =
    animationCount < 2 ||
    activeResult?.isCriticalSuccess === true ||
    activeResult?.isCriticalFailure === true;

  const {
    phase,
    displayValue,
    start: startAnimation,
  } = useRollAnimation(activeResult, animating, isDramatic);

  // When animation completes, increment counter
  useEffect(() => {
    if (phase === "done" && animating) {
      setAnimating(false);
      incrementAnimationCount();
      onResolved?.();
    }
  }, [phase, animating, incrementAnimationCount, onResolved]);

  // Start animation when result becomes available after resolve
  useEffect(() => {
    if (activeResult && animating && !animationTriggered.current) {
      animationTriggered.current = true;
      startAnimation();
    }
  }, [activeResult, animating, startAnimation]);

  const handleResolve = useCallback(
    async (modifiers: {
      flatBonus: number;
      advantage: boolean;
      disadvantage: boolean;
      bonusDice: number[];
      postRollFeedback?: PostRollFeedbackDecision;
    }) => {
      setResolving(true);
      setResolveError(null);
      animationTriggered.current = false;

      try {
        const ack = await callMcpTool(
          "resolve_save_roll",
          {
            saveRollId,
            playerModifiers: {
              advantage: modifiers.advantage,
              disadvantage: modifiers.disadvantage,
              flatBonus: modifiers.flatBonus,
              bonusDice: modifiers.bonusDice,
            },
            postRollFeedback: modifiers.postRollFeedback,
          },
          campaignId
        );
        if (
          ack.data &&
          typeof ack.data === "object" &&
          "postRollFeedback" in (ack.data as Record<string, unknown>)
        ) {
          const data = ack.data as Record<string, unknown>;
          setPendingPostRollFeedback(
            data.postRollFeedback as PostRollFeedbackEnvelope
          );
          return;
        }
        if (
          ack.data &&
          typeof ack.data === "object" &&
          "result" in (ack.data as Record<string, unknown>)
        ) {
          const data = ack.data as Record<string, unknown>;
          const result = data.result as SaveRollResult | undefined;
          if (result) {
            setPendingPostRollFeedback(null);
            setLocalResult(result);
          }
        }
        setAnimating(true);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to resolve save roll";
        logger.error({ err, saveRollId }, "Failed to resolve save roll");
        setResolveError(message);
      } finally {
        setResolving(false);
      }
    },
    [saveRollId, campaignId]
  );

  const handleRoll = useCallback(() => {
    handleResolve(DEFAULT_MODIFIERS);
  }, [handleResolve]);

  const handlePostRollFeedback = useCallback(
    (decision: PostRollFeedbackDecision) => {
      handleResolve({ ...DEFAULT_MODIFIERS, postRollFeedback: decision });
    },
    [handleResolve]
  );

  // ── Cancelled state ────────────────────────────────────────────────
  if (status === "cancelled") {
    return (
      <div className="my-2 rounded-lg border border-border bg-card p-4">
        <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
          {savingAbility.toUpperCase()} Save — DC {dcLabel}
        </div>
        <p className="text-muted-foreground text-sm">
          Cancelled
          {cancelledReason ? ` — ${cancelledReason}` : ""}
        </p>
      </div>
    );
  }

  // ── Resolved state ─────────────────────────────────────────────────
  const isResolved =
    (status === "resolved" && resolvedResult !== null) || localResult !== null;

  if (
    isResolved &&
    activeResult !== null &&
    phase !== "dim" &&
    phase !== "spin" &&
    phase !== "cycling"
  ) {
    const outcomeColor = getOutcomeColor(activeResult);
    const borderColor = getOutcomeBorderColor(activeResult);
    const badge = getOutcomeBadge(activeResult);
    const showGlow =
      activeResult.isCriticalSuccess && (phase === "done" || phase === "badge");
    const showVignette =
      activeResult.isCriticalFailure && (phase === "done" || phase === "badge");

    return (
      <div
        className={cn(
          "sc-border-transition my-2 rounded-lg border bg-card p-4",
          borderColor,
          showGlow && "sc-glow-pulse",
          showVignette && "sc-fail-vignette"
        )}
      >
        <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
          {savingAbility.toUpperCase()} Save — DC {dcLabel}
        </div>

        {/* Total */}
        <div
          className={cn(
            "sc-tabular-nums font-bold text-4xl",
            outcomeColor,
            phase === "landed" && "sc-scale-bounce-dramatic"
          )}
        >
          {activeResult.total}
        </div>

        {/* Breakdown */}
        <div className="mt-1 text-muted-foreground text-sm">
          {activeResult.roll} + {activeResult.modifierTotal}
        </div>

        {/* Outcome badge — show after animation completes or immediately if no animation ran */}
        {badge &&
          (phase === "badge" || phase === "done" || phase === "idle") && (
            <div
              className={cn(
                "sc-fade-in mt-3 inline-block rounded-full px-3 py-1 font-medium text-xs",
                badge.className
              )}
            >
              {activeResult.thresholdMet ? "✓" : "✗"} {badge.label}
            </div>
          )}
      </div>
    );
  }

  // ── Pending state (flat) ────────────────────────────────────────────
  // Always shows: header + DC + hydrated breakdown + Roll Save button.
  // No compact/expanded split, no gear toggle.
  const isDimming = phase === "dim" || phase === "spin" || phase === "cycling";
  const buttonsDisabled = resolving || animating;

  if (activePostRollFeedback) {
    return (
      <PostRollFeedbackOffer
        disabled={buttonsDisabled}
        envelope={activePostRollFeedback}
        onDecision={handlePostRollFeedback}
      />
    );
  }

  return (
    <div
      className={cn(
        "sc-border-transition relative my-2 overflow-hidden rounded-lg border border-border bg-card p-4"
      )}
    >
      {/* Animation overlay for dramatic sequence */}
      {isDimming && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          {phase === "spin" && (
            <span
              aria-hidden="true"
              className="sc-spin text-4xl"
              role="presentation"
            >
              {"🎲"}
            </span>
          )}
          {phase === "cycling" && (
            <span
              className={cn(
                "sc-tabular-nums font-bold text-4xl text-card-foreground"
              )}
            >
              {displayValue}
            </span>
          )}
        </div>
      )}

      <div className={cn(isDimming && "sc-dim")}>
        {/* Save label */}
        <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
          {savingAbility.toUpperCase()} Save
        </div>

        {/* DC */}
        <p className="mb-2 text-card-foreground text-sm">DC {dcLabel}</p>

        {/* Hydrated modifier preview — always visible when sheet is loaded.
            CAVEAT: the legacy save_rolls path folds only d20 + abilityMod today
            (proficiency deferred — "Future LAF-19"), so this sheet-sourced preview
            may over-promise vs. the actual roll for a proficient save. Tracked
            separately; do not "fix" by stripping proficiency from the preview. */}
        {sheet?.saves &&
          Array.isArray(
            sheet.saves[savingAbility as keyof typeof sheet.saves]
          ) && (
            <div className="mb-3 rounded border border-border/50 bg-muted/30 p-2">
              <div className="mb-1 text-[10px] text-muted-foreground uppercase tracking-wider">
                Hydrated modifiers (already applied)
              </div>
              <RollLineRow
                label={`${savingAbility} save`}
                mods={sheet.saves[savingAbility as keyof typeof sheet.saves]}
              />
            </div>
          )}

        {/* Resolve error */}
        {resolveError && (
          <div className="mb-2 rounded border border-destructive/30 bg-destructive/10 px-2 py-1 text-destructive text-xs">
            {resolveError}
          </div>
        )}

        {/* Roll button */}
        <button
          className="rounded bg-primary px-4 py-1.5 font-medium text-primary-foreground text-sm hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
          disabled={buttonsDisabled}
          onClick={handleRoll}
          type="button"
        >
          {resolving ? "Rolling..." : "Roll Save"}
        </button>
      </div>
    </div>
  );
}
